import { Injectable } from '@angular/core';
import { BehaviorSubject, Observable } from 'rxjs';
import { ConfirmationModalConfig } from './confirmation-modal.component';

export interface ConfirmationResult {
  confirmed: boolean;
  cancelled: boolean;
}

@Injectable({
  providedIn: 'root'
})
export class ConfirmationModalService {
  
  private modalStateSubject = new BehaviorSubject<{
    visible: boolean;
    config: ConfirmationModalConfig;
  }>({
    visible: false,
    config: {
      title: 'Confirm Action',
      message: 'Are you sure you want to proceed?',
      iconClass: 'fa fa-question-circle',
      iconColor: 'txt-color-orangeDark',
      confirmButtonText: 'Yes',
      cancelButtonText: 'No',
      confirmButtonClass: 'btn-danger',
      cancelButtonClass: 'btn-default',
      showCancelButton: true,
      showConfirmButton: true,
      animationClass: 'animated fadeIn fast'
    }
  });

  private confirmationResultSubject = new BehaviorSubject<ConfirmationResult | null>(null);

  constructor() { }

  /**
   * Show confirmation modal with custom configuration
   * @param config Configuration for the modal
   * @returns Observable that emits the confirmation result
   */
  showConfirmation(config: Partial<ConfirmationModalConfig>): Observable<ConfirmationResult> {
    const defaultConfig: ConfirmationModalConfig = {
      title: 'Confirm Action',
      message: 'Are you sure you want to proceed?',
      iconClass: 'fa fa-question-circle',
      iconColor: 'txt-color-orangeDark',
      confirmButtonText: 'Yes',
      cancelButtonText: 'No',
      confirmButtonClass: 'btn-danger',
      cancelButtonClass: 'btn-default',
      showCancelButton: true,
      showConfirmButton: true,
      animationClass: 'animated fadeIn fast'
    };

    const finalConfig = { ...defaultConfig, ...config };
    
    this.modalStateSubject.next({
      visible: true,
      config: finalConfig
    });

    // Return observable that will emit when user makes a choice
    return new Observable(observer => {
      const subscription = this.confirmationResultSubject.subscribe(result => {
        if (result !== null) {
          observer.next(result);
          observer.complete();
          subscription.unsubscribe();
        }
      });
    });
  }

  /**
   * Quick confirmation methods for common scenarios
   */
  
  // Delete confirmation
  showDeleteConfirmation(itemName?: string): Observable<ConfirmationResult> {
    return this.showConfirmation({
      title: 'Delete Confirmation',
      message: itemName ? `Are you sure you want to delete "${itemName}"?` : 'Are you sure you want to delete this item?',
      iconClass: 'fa fa-trash',
      iconColor: 'txt-color-red',
      confirmButtonText: 'Delete',
      cancelButtonText: 'Cancel',
      confirmButtonClass: 'btn-danger',
      cancelButtonClass: 'btn-default'
    });
  }

  // Refresh confirmation
  showRefreshConfirmation(dataType?: string): Observable<ConfirmationResult> {
    return this.showConfirmation({
      title: 'Refresh Confirmation',
      message: dataType ? `Are you sure you want to refresh the ${dataType} data?` : 'Are you sure you want to refresh the data?',
      iconClass: 'fa fa-refresh',
      iconColor: 'txt-color-orangeDark',
      confirmButtonText: 'Yes',
      cancelButtonText: 'No',
      confirmButtonClass: 'btn-danger',
      cancelButtonClass: 'btn-default'
    });
  }

  // Save confirmation
  showSaveConfirmation(): Observable<ConfirmationResult> {
    return this.showConfirmation({
      title: 'Save Confirmation',
      message: 'Are you sure you want to save these changes?',
      iconClass: 'fa fa-save',
      iconColor: 'txt-color-green',
      confirmButtonText: 'Save',
      cancelButtonText: 'Cancel',
      confirmButtonClass: 'btn-success',
      cancelButtonClass: 'btn-default'
    });
  }

  // Reset confirmation
  showResetConfirmation(): Observable<ConfirmationResult> {
    return this.showConfirmation({
      title: 'Reset Confirmation',
      message: 'Are you sure you want to reset all changes? This action cannot be undone.',
      iconClass: 'fa fa-undo',
      iconColor: 'txt-color-orangeDark',
      confirmButtonText: 'Reset',
      cancelButtonText: 'Cancel',
      confirmButtonClass: 'btn-warning',
      cancelButtonClass: 'btn-default'
    });
  }

  // Logout confirmation
  showLogoutConfirmation(): Observable<ConfirmationResult> {
    return this.showConfirmation({
      title: 'Logout Confirmation',
      message: 'Are you sure you want to logout? Any unsaved changes will be lost.',
      iconClass: 'fa fa-sign-out',
      iconColor: 'txt-color-orangeDark',
      confirmButtonText: 'Logout',
      cancelButtonText: 'Cancel',
      confirmButtonClass: 'btn-danger',
      cancelButtonClass: 'btn-default'
    });
  }

  /**
   * Get current modal state
   */
  getModalState(): Observable<{ visible: boolean; config: ConfirmationModalConfig }> {
    return this.modalStateSubject.asObservable();
  }

  /**
   * Handle confirmation result
   */
  confirm(): void {
    this.confirmationResultSubject.next({ confirmed: true, cancelled: false });
    this.hideModal();
  }

  /**
   * Handle cancellation result
   */
  cancel(): void {
    this.confirmationResultSubject.next({ confirmed: false, cancelled: true });
    this.hideModal();
  }

  /**
   * Hide the modal
   */
  hideModal(): void {
    this.modalStateSubject.next({
      visible: false,
      config: this.modalStateSubject.value.config
    });
    this.confirmationResultSubject.next(null);
  }

  /**
   * Check if modal is currently visible
   */
  isVisible(): boolean {
    return this.modalStateSubject.value.visible;
  }
}
